import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(ScrollTrigger, SplitText);

/* -------------------------------------------------------------
   Masked heading reveal — H1/H2 + [data-split="heading"]
   Lines slide up out of an overflow mask on scroll into view.
   Works with Lenis: ScrollTrigger.update is driven by Lenis in main.js.
   ------------------------------------------------------------- */

const splitConfig = {
  lines: { duration: 0.8, stagger: 0.08 },
  words: { duration: 0.6, stagger: 0.06 },
  chars: { duration: 0.4, stagger: 0.01 }
};

function initMaskTextScrollReveal() {
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Auto-tag every H1/H2 not already tagged and not managed by a reveal group.
  //
  // [data-cycle-highlight] is excluded: the text-cycle block does its own
  // line-splitting to draw a yellow panel behind each line, and this reveal
  // splits the same element. Whichever ran second replaced the other's DOM —
  // which is why the yellow bars appeared and then vanished once the page had
  // finished loading. One owner per element.
  document.querySelectorAll("h1, h2").forEach(h => {
    if (
      !h.hasAttribute("data-split") &&
      !h.hasAttribute("data-cycle-highlight") &&
      !h.closest("[data-reveal-group]")
    ) {
      h.setAttribute("data-split", "heading");
    }
  });

  document.querySelectorAll('[data-split="heading"]').forEach(heading => {
    // Reveal the CSS-hidden heading (visibility: hidden in typography.css)
    gsap.set(heading, { autoAlpha: 1 });

    if (prefersReduced) return;

    // Clean up a previous split (e.g. re-init after a page transition)
    if (heading._splitInstance) {
      heading._splitInstance.revert();
      heading._splitInstance = null;
    }

    // Split type — default 'lines', override via data-split-reveal="words|chars"
    const type = heading.dataset.splitReveal || "lines";
    const typesToSplit =
      type === "lines" ? ["lines"] :
      type === "words" ? ["lines", "words"] :
      ["lines", "words", "chars"];

    heading._splitInstance = SplitText.create(heading, {
      type: typesToSplit.join(", "), // split into required elements
      mask: "lines",                 // wrap each line in an overflow:hidden div
      autoSplit: true,               // re-split on font load / resize
      linesClass: "line",
      wordsClass: "word",
      charsClass: "letter",
      onSplit: function (instance) {
        const targets = instance[type];
        const config = splitConfig[type];

        // Fire immediately (no ScrollTrigger) when the heading is ALREADY
        // visible at creation — hero headings, and anything else that happens
        // to sit within the viewport on load. A scroll-into-view trigger with
        // `once` never fires for an element already past its start position, so
        // an above-the-fold heading would stay masked (lines at yPercent:110)
        // until a scroll or resize nudged ScrollTrigger. This is that "heading
        // in view doesn't animate until you scroll" bug. Check the live rect
        // rather than only .block__hero so every in-view heading plays at once.
        const rect = heading.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        const immediate = heading.hasAttribute("data-split-immediate") ||
          heading.closest(".block__hero") || inView;

        if (immediate) {
          return gsap.from(targets, {
            yPercent: 110,
            duration: config.duration,
            stagger: config.stagger,
            ease: "expo.out"
          });
        }

        const tween = gsap.from(targets, {
          yPercent: 110,
          duration: config.duration,
          stagger: config.stagger,
          ease: "expo.out",
          scrollTrigger: {
            trigger: heading,
            start: "clamp(top 80%)",
            once: true
          }
        });

        // SplitText's onSplit can run async (after font load), which can be
        // after the global ScrollTrigger.refresh(). Re-evaluate this trigger
        // against the current scroll position.
        tween.scrollTrigger && tween.scrollTrigger.refresh();

        // Stash the tween so the load-time safety net (revealStuckHeadings) can
        // rescue it if its ScrollTrigger was built against a pre-load layout and
        // never fires — the "heading only appears on resize" bug.
        heading._revealTween = tween;

        return tween;
      }
    });
  });
}

/* Safety net for the "heading only appears on resize" bug. Split headings hide
   their lines under an overflow mask (yPercent:110) and reveal them via a
   ScrollTrigger. When that trigger is built against a pre-load layout (images
   0-height, fonts unsettled) it can resolve to a stale position and never fire,
   leaving the heading blank though its container is autoAlpha:1. Run this after
   window.load / fonts.ready: any split heading now in the viewport whose reveal
   tween hasn't progressed gets played directly. Idempotent and cheap. */
export function revealStuckHeadings() {
  document.querySelectorAll('[data-split="heading"]').forEach(heading => {
    const tween = heading._revealTween;
    if (!tween) return;
    const r = heading.getBoundingClientRect();
    const inView = r.top < window.innerHeight && r.bottom > 0;
    // progress 0 and not active => the trigger never fired for this element.
    const stuck = tween.progress() === 0 && !(tween.scrollTrigger && tween.scrollTrigger.isActive);
    if (inView && stuck) {
      tween.scrollTrigger && tween.scrollTrigger.kill();
      tween.play();
    }
  });
}

function initContentRevealScroll() {
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Helpers
  const num = (v, fb) => {
    const n = parseFloat(v);
    return isNaN(n) ? fb : n;
  };
  const val = (el, name, fb) => el.getAttribute(name) ?? fb;
  const msToSec = v => (isNaN(parseFloat(v)) ? 0 : parseFloat(v) / 1000);
  const isTexty = el => el.matches("p, h1, h2, h3, h4, h5, h6, li, a, span, strong, em");

  // Initial state per element (Safari-friendly)
  function setInitial(el, cfg) {
    const { type, direction, distance } = cfg;

    // Force own compositing layer to avoid Safari pop-in
    const layerHint = { transform: "translateZ(0.001px)" };

    if (type === "fade") {
      if (isTexty(el)) {
        // Safari fix: opacity-only for text; no visibility flips
        gsap.set(el, {
          ...layerHint,
          opacity: 0.001, // hairline to avoid first-frame jump
          y: direction === "up" ? distance : direction === "down" ? -distance : 0,
          x: direction === "left" ? distance : direction === "right" ? -distance : 0
        });
      } else {
        gsap.set(el, {
          ...layerHint,
          autoAlpha: 0,
          y: direction === "up" ? distance : direction === "down" ? -distance : 0,
          x: direction === "left" ? distance : direction === "right" ? -distance : 0
        });
      }
      return;
    }

    if (type === "slide") {
      const tOrigin =
        direction === "up" || direction === "down"
          ? `${direction === "down" ? "top" : "bottom"} center`
          : `center ${direction === "right" ? "left" : "right"}`;

      if (isTexty(el)) {
        gsap.set(el, {
          ...layerHint,
          opacity: 0.001,
          x: direction === "left" ? -distance : direction === "right" ? distance : 0,
          y: direction === "up" ? distance : direction === "down" ? -distance : 0,
          transformOrigin: tOrigin
        });
      } else {
        gsap.set(el, {
          ...layerHint,
          autoAlpha: 0,
          x: direction === "left" ? -distance : direction === "right" ? distance : 0,
          y: direction === "up" ? distance : direction === "down" ? -distance : 0,
          transformOrigin: tOrigin
        });
      }
      return;
    }

    if (type === "zoom") {
      if (isTexty(el)) {
        gsap.set(el, { ...layerHint, opacity: 0.001, scale: 0.94 });
      } else {
        gsap.set(el, { ...layerHint, autoAlpha: 0, scale: 0.94 });
      }
      return;
    }

    if (type === "blur") {
      // texty or not, set both filters for Safari
      const base = {
        ...layerHint,
        y: distance,
        filter: "blur(10px)",
        WebkitFilter: "blur(10px)"
      };
      if (isTexty(el)) {
        gsap.set(el, { ...base, opacity: 0.001 });
      } else {
        gsap.set(el, { ...base, autoAlpha: 0 });
      }
      return;
    }

    if (type === "wipe") {
      let clip;
      switch (direction) {
        case "up":    clip = "inset(100% 0% 0% 0%)"; break; // bottom -> top
        case "down":  clip = "inset(0% 0% 100% 0%)"; break; // top -> bottom
        case "left":  clip = "inset(0% 100% 0% 0%)"; break; // right -> left
        case "right": clip = "inset(0% 0% 0% 100%)"; break; // left -> right
        default:      clip = "inset(100% 0% 0% 0%)";
      }
      // For wipes, we keep element visible; mask does the hiding
      gsap.set(el, {
        ...layerHint,
        clipPath: clip,
        WebkitClipPath: clip
      });
      return;
    }

    // fallback
    if (isTexty(el)) {
      gsap.set(el, { ...layerHint, opacity: 0.001, y: distance });
    } else {
      gsap.set(el, { ...layerHint, autoAlpha: 0, y: distance });
    }
  }

  // Tween builder (Safari hardened)
  function addRevealToTimeline(tl, el, cfg, atTime = 0) {
    const { type, duration, ease } = cfg;
    const common = { duration, ease, force3D: true, immediateRender: false };

    if (type === "fade" || type === "slide") {
      if (isTexty(el)) {
        tl.to(el, { opacity: 1, x: 0, y: 0, ...common, onComplete: () => gsap.set(el, { clearProps: "opacity,transform" }) }, atTime);
      } else {
        tl.to(el, { autoAlpha: 1, x: 0, y: 0, ...common, onComplete: () => gsap.set(el, { clearProps: "all" }) }, atTime);
      }
      return;
    }

    if (type === "zoom") {
      if (isTexty(el)) {
        tl.to(el, { opacity: 1, scale: 1, ...common, onComplete: () => gsap.set(el, { clearProps: "opacity,transform" }) }, atTime);
      } else {
        tl.to(el, { autoAlpha: 1, scale: 1, ...common, onComplete: () => gsap.set(el, { clearProps: "all" }) }, atTime);
      }
      return;
    }

    if (type === "blur") {
      if (isTexty(el)) {
        tl.to(el, {
          opacity: 1,
          filter: "blur(0px)",
          WebkitFilter: "blur(0px)",
          x: 0, y: 0,
          ...common,
          onComplete: () => gsap.set(el, { clearProps: "opacity,filter,WebkitFilter,transform" })
        }, atTime);
      } else {
        tl.to(el, {
          autoAlpha: 1,
          filter: "blur(0px)",
          WebkitFilter: "blur(0px)",
          x: 0, y: 0,
          ...common,
          onComplete: () => gsap.set(el, { clearProps: "all" })
        }, atTime);
      }
      return;
    }

    if (type === "wipe") {
      tl.to(el, {
        clipPath: "inset(0% 0% 0% 0%)",
        WebkitClipPath: "inset(0% 0% 0% 0%)",
        ...common,
        onComplete: () => gsap.set(el, { clearProps: "clipPath,WebkitClipPath,willChange" })
      }, atTime);
      return;
    }

    // fallback
    if (isTexty(el)) {
      tl.to(el, { opacity: 1, x: 0, y: 0, ...common, onComplete: () => gsap.set(el, { clearProps: "opacity,transform" }) }, atTime);
    } else {
      tl.to(el, { autoAlpha: 1, x: 0, y: 0, ...common, onComplete: () => gsap.set(el, { clearProps: "all" }) }, atTime);
    }
  }

  const ctx = gsap.context(() => {
    document.querySelectorAll("[data-reveal-group]").forEach(groupEl => {
      // Group defaults (override via attributes on the group)
      const groupStaggerSec = num(groupEl.getAttribute("data-stagger"), 100) / 1000;
      const groupDistance   = val(groupEl, "data-distance", "2em");
      const triggerStart    = val(groupEl, "data-start", "top 80%");
      const groupType       = val(groupEl, "data-reveal", "fade");
      const groupDirection  = val(groupEl, "data-direction", "up");
      const groupDuration   = num(groupEl.getAttribute("data-duration"), 1.5); // per-group duration
      const groupEase       = val(groupEl, "data-ease", "power4.inOut");

      if (prefersReduced) {
        gsap.set(groupEl, {
          clearProps: "all",
          y: 0,
          opacity: 1,
          clipPath: "none",
          filter: "none",
          scale: 1
        });
        return;
      }

      // Determine targets (direct children or self)
      const directChildren = Array.from(groupEl.children).filter(el => el.nodeType === 1);
      const targets = directChildren.length ? directChildren : [groupEl];

      // Build slots with nested support
      const slots = [];
      targets.forEach(child => {
        const nestedGroup = child.matches("[data-reveal-group-nested]")
          ? child
          : child.querySelector(":scope [data-reveal-group-nested]");

        if (nestedGroup) {
          const includeParent =
            child.getAttribute("data-ignore") === "false" ||
            nestedGroup.getAttribute("data-ignore") === "false";
          slots.push({ type: "nested", parentEl: child, nestedEl: nestedGroup, includeParent });
        } else {
          slots.push({ type: "item", el: child });
        }
      });

      // Initial states
      slots.forEach(slot => {
        if (slot.type === "item") {
          const cfg = {
            type:      val(slot.el, "data-reveal", groupType),
            direction: val(slot.el, "data-direction", groupDirection),
            distance:  val(slot.el, "data-distance", groupDistance),
            duration:  num(slot.el.getAttribute("data-duration"), groupDuration),
            ease:      val(slot.el, "data-ease", groupEase),
            delaySec:        msToSec(slot.el.getAttribute("data-delay")),
            selfStaggerSec:  msToSec(slot.el.getAttribute("data-stagger")) // item-level offset
          };
          setInitial(slot.el, cfg);
        } else {
          if (slot.includeParent) {
            const cfgP = {
              type:      val(slot.parentEl, "data-reveal", groupType),
              direction: val(slot.parentEl, "data-direction", groupDirection),
              distance:  val(slot.parentEl, "data-distance", groupDistance),
              duration:  num(slot.parentEl.getAttribute("data-duration"), groupDuration),
              ease:      val(slot.parentEl, "data-ease", groupEase),
              delaySec:       msToSec(slot.parentEl.getAttribute("data-delay")),
              selfStaggerSec: msToSec(slot.parentEl.getAttribute("data-stagger"))
            };
            setInitial(slot.parentEl, cfgP);
          }
          const nestedChildren = Array.from(slot.nestedEl.children);
          const nestedDistance = val(slot.nestedEl, "data-distance", groupDistance);
          nestedChildren.forEach(nestedChild => {
            const cfgC = {
              type:      val(nestedChild, "data-reveal", val(slot.nestedEl, "data-reveal", groupType)),
              direction: val(nestedChild, "data-direction", val(slot.nestedEl, "data-direction", groupDirection)),
              distance:  val(nestedChild, "data-distance", nestedDistance),
              duration:  num(nestedChild.getAttribute("data-duration"), num(slot.nestedEl.getAttribute("data-duration"), groupDuration)),
              ease:      val(nestedChild, "data-ease", val(slot.nestedEl, "data-ease", groupEase)),
              delaySec:       msToSec(nestedChild.getAttribute("data-delay")),
              selfStaggerSec: msToSec(nestedChild.getAttribute("data-stagger"))
            };
            setInitial(nestedChild, cfgC);
          });
        }
      });

      // Reveal sequence
      ScrollTrigger.create({
        trigger: groupEl,
        start: triggerStart,
        once: true,
        onEnter: () => {
          const tl = gsap.timeline();

          slots.forEach((slot, slotIndex) => {
            const slotTime = slotIndex * groupStaggerSec;

            if (slot.type === "item") {
              const cfg = {
                type:      val(slot.el, "data-reveal", groupType),
                direction: val(slot.el, "data-direction", groupDirection),
                distance:  val(slot.el, "data-distance", groupDistance),
                duration:  num(slot.el.getAttribute("data-duration"), groupDuration),
                ease:      val(slot.el, "data-ease", groupEase),
                delaySec:        msToSec(slot.el.getAttribute("data-delay")),
                selfStaggerSec:  msToSec(slot.el.getAttribute("data-stagger"))
              };
              addRevealToTimeline(
                tl,
                slot.el,
                cfg,
                slotTime + (cfg.delaySec || 0) + (cfg.selfStaggerSec || 0)
              );
            } else {
              if (slot.includeParent) {
                const cfgP = {
                  type:      val(slot.parentEl, "data-reveal", groupType),
                  direction: val(slot.parentEl, "data-direction", groupDirection),
                  distance:  val(slot.parentEl, "data-distance", groupDistance),
                  duration:  num(slot.parentEl.getAttribute("data-duration"), groupDuration),
                  ease:      val(slot.parentEl, "data-ease", groupEase),
                  delaySec:       msToSec(slot.parentEl.getAttribute("data-delay")),
                  selfStaggerSec: msToSec(slot.parentEl.getAttribute("data-stagger"))
                };
                addRevealToTimeline(
                  tl,
                  slot.parentEl,
                  cfgP,
                  slotTime + (cfgP.delaySec || 0) + (cfgP.selfStaggerSec || 0)
                );
              }

              const nestedMs = parseFloat(slot.nestedEl.getAttribute("data-stagger"));
              const nestedStaggerSec = isNaN(nestedMs) ? groupStaggerSec : nestedMs / 1000;

              Array.from(slot.nestedEl.children).forEach((nestedChild, nestedIndex) => {
                const cfgC = {
                  type:      val(nestedChild, "data-reveal", val(slot.nestedEl, "data-reveal", groupType)),
                  direction: val(nestedChild, "data-direction", val(slot.nestedEl, "data-direction", groupDirection)),
                  distance:  val(nestedChild, "data-distance", val(slot.nestedEl, "data-distance", groupDistance)),
                  duration:  num(nestedChild.getAttribute("data-duration"), num(slot.nestedEl.getAttribute("data-duration"), groupDuration)),
                  ease:      val(nestedChild, "data-ease", val(slot.nestedEl, "data-ease", groupEase)),
                  delaySec:       msToSec(nestedChild.getAttribute("data-delay")),
                  selfStaggerSec: msToSec(nestedChild.getAttribute("data-stagger"))
                };
                addRevealToTimeline(
                  tl,
                  nestedChild,
                  cfgC,
                  slotTime +
                    nestedIndex * nestedStaggerSec +
                    (cfgC.delaySec || 0) +
                    (cfgC.selfStaggerSec || 0)
                );
              });
            }
          });
        }
      });
    });
  });

  return () => ctx.revert();
}

export { initContentRevealScroll, initMaskTextScrollReveal };
